<template>
  <div class="h-full flex flex-col space-y-2 px-2 py-2">
    <div v-show="show_genie_feedback === false" id="prompt-editor" class="h-full">
      <PromptEditor
        id="prompt-written"
        :init="editorOptions"
        api-key="no-api-key"
        :value="prompt_adjusted_with_links"
      />
    </div>
    <div v-show="show_genie_feedback === true" class="genie-feedback h-full html-genie-content">
      <div v-if="genie_feedback.overall_feedback !== ''" class="rounded">
        <div class="flex justify-between items-center px-3 mb-2">
          <div class="font-manrope text-xs text-gray-800 flex items-center space-x-2">
            <i class="fa-solid fa-circle-info"></i>
            <span>Genie’s feedback is AI-generated and still in beta. It can help refine your ideas, but your own judgment matters most.</span>
          </div>
          <button
            @click="handleRegenerateGenieFeedback()"
            class="w-7 h-7 bg-blueGray-50 border border-solid hover:bg-blueGray-100 text-blueGray-600 hover:text-blueGray-700 border-blueGray-600 rounded-full"
          >
            <i class="fa-solid text-sm fa-rotate " :class="is_regenerating === true ? 'animate-spin' : ''"></i>
          </button>
        </div>
        <div v-if="is_regenerating === true" class="w-full">
          <div class="flex items-start space-x-3 mt-3">
            <div class="w-full flex flex-col animate-pulse space-y-3">
              <div class="h-4 genie-background rounded w-full"></div>
              <div class="h-4 genie-background rounded w-7/12"></div>
              <div class="h-4 genie-background rounded w-10/12"></div>
              <div class="h-4 genie-background rounded w-7/12"></div>
              <div class="h-4 genie-background rounded w-full"></div>
              <div class="h-4 genie-background rounded w-10/12"></div>
              <div class="h-4 genie-background rounded w-8/12"></div>
              <div class="h-4 genie-background rounded w-6/12"></div>
              <div class="h-4 genie-background rounded w-full"></div>
              <div class="h-4 genie-background rounded w-4/12"></div>
              <div class="h-4 genie-background rounded w-9/12"></div>
              <div class="h-4 genie-background rounded w-full"></div>
            </div>
          </div>
        </div>
        <div v-else class="w-full bg-white rounded flex flex-col h-full overflow-y-auto">
          <div class="flex-1 overflow-y-auto px-3 py-2 space-y-4">
            <div class="flex flex-col space-y-2">
              <div class="flex flex-col space-y-3">
                <div class="flex justify-between items-center space-x-3">
                  <div class="flex items-center space-x-3">
                    <i class="fa-regular text-xl fa-comment text-blueGray-800"></i>
                    <h6 class="font-manrope font-semibold text-gray-800 text-lg">
                      General Feedback
                    </h6>
                  </div>
                </div>
                <div class="font-manrope text-gray-800 text-sm  font-light">
                  {{ genie_feedback.overall_feedback }}
                </div>
              </div>
            </div>
          </div>
          <div class="border-b border-gray-200 mt-3 mb-3"></div>
          <div class="flex flex-col space-y-2 py-4 px-3">
            <div class="flex items-center space-x-3">
              <i class="fa-regular fa-thumbs-up text-xl text-blue-500"></i>
              <div v-if="is_regenerating === true" class="w-32 genie-background"></div>
              <h6 v-else class="font-manrope font-semibold text-gray-800 text-base">
                Strengths
              </h6>
            </div>
            <ul v-for="strength in genie_feedback.strengths" class="list-disc ml-5 text-sm font-manrope text-gray-800" >
              <li>{{ strength }}</li>
            </ul>
          </div>
          <div class="flex flex-col space-y-2 px-3 py-4 mb-5">
            <div class="flex items-center space-x-3">
              <i class="fa-solid fa-rocket text-orange-500 text-xl"></i>
              <h6 class="font-manrope font-semibold text-gray-800 text-base">
                Opportunities for Improvement...
              </h6>
            </div>
            <ul v-for="improvment in genie_feedback.improvments" class="list-disc ml-5 text-sm font-manrope text-gray-800">
              <li>{{ improvment }}</li>
            </ul>
          </div>
        </div>
      </div>
      <div v-else class="w-full h-full flex flex-col justify-center items-center space-y-5">
        <div class="flex justify-center items-center space-x-3">
          <div class="relative">
            <img class="rounded-full" src="/static/roundtable_options_create/genie_icon.svg" />
          </div>
          <h5 class="font-poppins font-semibold text-xl text-gray-700">Genie Feedback</h5>
        </div>
        <div class="px-32 font-poppins text-blueGray-800 text-center">
          <div>
            The Genie <b class="font-semibold">analyzes your response</b> in real time, compares it to <b class="font-semibold">predefined standards and provides constructive</b> feedback. It highlights <b class="font-semibold">strengths, identifies areas for improvement and offers actionable suggestions</b> to enhance learning outcomes.
          </div>
        </div>
        <div class="flex items-center justify-center space-x-2">
          <button @click="handleStartGenerateFeedback()" class="px-3 rounded py-2 bg-pinky flex items-center space-x-2 hover:bg-pinky-dark text-white border border-solid border-pinky">
            <span>{{ is_generating_feedback === true ? 'Generating' : 'Generate Feedback' }}</span>
            <div v-if="is_generating_feedback === true" class="loader"></div>
          </button>
        </div>
      </div>
    </div>
    <div class="flex justify-end items-center" v-if="show_genie_feedback === false">
      <ViewCriteriaModal
        v-if="getCurrentRT.enable_grading && getCurrentRT.points_settings.peer_rewards.length"
        :rewards="getCurrentRT.points_settings.peer_rewards"
        :criteriaScale="Number(getCurrentRT.point_cap)"
      />
    </div>
  </div>
</template>
<script>
  import Vue from 'vue';
  import { mapGetters } from 'vuex';
  import onlineRoundtableSockets from 'mixins/sockets/onlineRoundtableSocketEvents';
  import courseSocketMixin from 'mixins/sockets/courseSocketEvents';
  import { EventBus } from '../../../utils/event-bus';
  import Axios from 'axios';
  import { cloneDeep } from 'lodash';
  export default Vue.extend({
    data() {
      return {
        submission: {},
        editorOptions: {
          base_url: '/static/tinymce7',
          menubar: false,
          height: '100%',
          disabled: true,
          license_key: 'gpl',
          plugins: 'lists link image preview visualblocks visualchars quickbars',
          toolbar: false,
          skin_url: '/static/tinymce/parlay/ui/Parlay/',
          content_css: '/static/tinymce/prompt-style.css',
          branding: false,
          statusbar: false,
          resize: false,
          forced_root_block: 'h1',
          media_live_embeds: true,
          indent: false,
          valid_elements: '*[*]',
          elementpath: true,
          extended_valid_elements: '*[*]',
          cleanup: false,
          verify_html: false,
          quickbars_selection_toolbar: 'bold italic underline | genieRevise | bullist numlist | blockquote quicklink',
          sandbox_iframes: false,
          setup(editor) {
            const stripSandbox = (html) => typeof html === 'string' ? html.replace(/<iframe\b([^>]*)\s*sandbox="[^"]*"?/gi, '<iframe$1') : html;

            editor.on('BeforeSetContent', (e) => { e.content = stripSandbox(e.content); });
            editor.on('PostProcess', (e) => { if (e.get) e.content = stripSandbox(e.content); });

            editor.on('SetContent', () => {
              editor.dom.select('iframe').forEach((node) => {
                if (!node.getAttribute('allowfullscreen')) node.setAttribute('allowfullscreen', '');
                if (!node.getAttribute('allow')) node.setAttribute('allow','accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
              });
            });
          },
        },
        genie_feedback: {
          overall_feedback: '',
          strengths: [],
          improvments: [],
        },
        show_genie_feedback: false,
        is_regenerating: false,
        is_enable_to_regenarate: false,
        is_generating_feedback: false
      };
    },
    components: {
      PromptEditor: () => import('@tinymce/tinymce-vue'),
      ViewCriteriaModal: () => import('../../Criterias/ViewCriteriaModal.vue'),
    },
    computed: {
      ...mapGetters([
        'getProfile',
        'getCurrentRT',
        'getCourseDetail',
        'hasTeacherPermissionsInCourse',
        'getMyRTUserDetails'
      ]),
      currentSubmission: function () {
        return this.getCurrentRT.submissions.find(
          (s) => s.id === this.getMyRTUserDetails.submission_id
        );
      },
      prompt_adjusted_with_links() {
        let prompt = this.getCurrentRT.prompt;
        let listItemsString = '';
        if (this.getCurrentRT.links.length > 0) {
          this.getCurrentRT.links.forEach((link) => {
            const linkTag = `<li> <a href="${link.link}"target="_blank"> ${link.title} </a></li>`;
            listItemsString = listItemsString.concat(linkTag);
          });
          prompt = prompt.concat(`<hr /> Links: <ol> ${listItemsString} </ol>`);
        }
        return prompt;
      },
      connected_to_google() {
        return (
          Object.keys(this.getProfile.google_integration).length &&
          this.getProfile.google_integration.connected
        );
      },
      connected_to_rt_integration() {
        if (
          this.getProfile.edlink_integrations.length &&
          this.getCurrentRT.edlink_integrations.length
        ) {
          const integration = this.getProfile.edlink_integrations.find(
            (i) => i.id === this.getCurrentRT.edlink_integrations[0].id
          );
          return !!integration;
        }
        return false;
      },
      submission_google_integration() {
        return (
          this.getCurrentRT.google_classroom_course_work_id !== null &&
          this.submission.google_classroom_integration.connected
        );
      },
      submission_rt_integration() {
        const { edlink_integrations: submission_integrations } =
          this.submission;
        const { edlink_integrations: rt_integrations } = this.getCurrentRT;
        if (
          rt_integrations &&
          rt_integrations.length &&
          submission_integrations.length
        ) {
          const rt_integration = rt_integrations[0]; // just one per roundtable
          return submission_integrations.find(
            (sub) => sub.id === rt_integration.id
          );
        }
        return false;
      },
    },
    mixins: [onlineRoundtableSockets, courseSocketMixin],
    methods: {
      handleRegenerateGenieFeedback: async function() {
        try {
          if (!this.getCurrentRT.is_genie_feedback_enable) {
            return;
          }
          this.is_regenerating = true;

          const data = {
            roundtable_id: this.getCurrentRT.id,
            submission: this.currentSubmission,
            student_name: this.getProfile.name          
          };
          const feedback = await Axios.post('/api/feedback/regenerate-feedback', data);
          const cloned_rt = cloneDeep(this.getCurrentRT);

          cloned_rt.submissions.map((submission) => {
            if (submission.id === this.currentSubmission.id) {
              submission.submission_history.unshift(feedback.data.data.new_history_feedback);
            }
          });
          this.$store.commit('UPDATE_RT_DETAILS', cloned_rt);
          this.genie_feedback.overall_feedback = feedback.data.data.feedback.overall_feedback;
          this.genie_feedback.strengths = feedback.data.data.feedback.strengths;
          this.genie_feedback.improvments = feedback.data.data.feedback.improvments;
          this.is_regenerating = false;
        } catch (error) {
          this.is_regenerating = false;
          return console.log(error);
        }
      },
      handleStartGenerateFeedback: async function () {
        try {
          if (!this.getCurrentRT.is_genie_feedback_enable) {
            return;
          }
          this.is_generating_feedback = true;
        
          const data = {
            roundtable_id: this.getCurrentRT.id,
            submission: this.currentSubmission,
            student_name: this.getProfile.name
          };
          const feedback = await Axios.post('/api/feedback/generate-feedback',data);

          const cloned_rt = cloneDeep(this.getCurrentRT);

          cloned_rt.submissions.map((submission) => {
            if (submission.id === this.currentSubmission.id) {
              submission.submission_history.unshift(feedback.data.data.new_history_feedback);
            }
          });
          this.$store.commit('UPDATE_RT_DETAILS', cloned_rt);
          this.genie_feedback.overall_feedback = feedback.data.data.feedback.overall_feedback;
          this.genie_feedback.strengths = feedback.data.data.feedback.strengths;
          this.genie_feedback.improvments = feedback.data.data.feedback.improvments;
          this.is_generating_feedback = false;
        } catch (error) {
          this.is_generating_feedback = false;
          return console.log(error);
        }
      },
      getMySubmission() {
        return this.getCurrentRT.submissions.find(
          (x) => x.user.id === this.getProfile.id
        );
      },
      getMyParticipation() {
        return this.getCurrentDiscussion.participations.find(
          (x) => x.user.id === this.getProfile.id
        );
      },
      mySubmission() {
        const sub = this.$route.params.roundtableID ? this.getMySubmission() : this.getMyParticipation();
        this.submission = sub;
      },
    },
    mounted: async function() {
      const { courseID, roundtableID } = this.$route.params;
      this.setupCourseSocket(courseID);
      this.leaveRoundtableSocket(roundtableID);
      this.setupRoundtableSocket(roundtableID);

      await this.mySubmission();

      const history = this.submission.submission_history || [];

      const validHistory = history.filter(entry => entry.genie_feedback_result != null);

      const roundtableSubmission_history = this.currentSubmission.submission_history.filter((sub) => sub.genie_feedback_result !== null);

      if ((this.submission_google_integration && !this.connected_to_google) || (this.submission_rt_integration && !this.connected_to_rt_integration)) {
        sessionStorage.setItem('course_id', this.getCourseDetail.id);
        sessionStorage.setItem('activity_id', this.getCurrentRT.id);
        sessionStorage.setItem('activity_type', 'online');
        const route = {
          name: 'student.settings',
          params: { courseID: this.$route.params.courseID },
          query: { roundtableID: this.$route.params.roundtableID },
        };
        this.$router.push(route);
      }

      EventBus.$on('handleOpenGenieFeedback', () => {
        this.show_genie_feedback = !this.show_genie_feedback;
      });

      if (validHistory.length > 0 && this.getCurrentRT.is_genie_feedback_enable === true) {
        const parsed_genie_feedback = JSON.parse(roundtableSubmission_history[0].genie_feedback_result);
        this.genie_feedback.overall_feedback = parsed_genie_feedback.overall_feedback;
        this.genie_feedback.strengths = parsed_genie_feedback.strengths;
        this.genie_feedback.improvments = parsed_genie_feedback.improvments;
      }
      
    },
    beforeDestroy() {
      this.leaveRoundtableSocket(this.$route.params.roundtableID);
    },
  });
</script>

<style scoped>
  .content {
    font-family: Arial, sans-serif;
    padding: 20px;
    max-width: 800px;
    margin: 0 auto;
  }

  p {
    color: #374151;
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 1em;
  }

  p span {
    display: inline !important;
  }

  .genie-background {
    background: rgb(12, 112, 168);
    background: linear-gradient(
      47deg,
      rgba(12, 112, 168, 0.2) 0%,
      rgba(255, 35, 165, 0.3) 100%
    );
  }

  .genie-popup {
    background: rgb(0, 163, 255);
    background: linear-gradient(
      47deg,
      rgba(0, 163, 255, 1) 0%,
      rgba(255, 35, 198, 1) 100%
    );
  }

  .loader,
  .loader:after {
    border-radius: 50%;
    width: 15px;
    height: 15px;
  }
  
  .loader {
    border-top: 3px solid rgba(255, 255, 255, 0.2);
    border-right: 3px solid rgba(255, 255, 255, 0.2);
    border-bottom: 3px solid rgba(255, 255, 255, 0.2);
    border-left: 3px solid #ffffff;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: load8 1.1s infinite linear;
    animation: load8 1.1s infinite linear;
  }
  @-webkit-keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
  @keyframes load8 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
</style>



// WEBPACK FOOTER //
// promptTab.vue?63879d96